import _ from 'lodash'
import { CSSObject } from '@emotion/react'
import { Property } from 'csstype'

//
//
// FONTS
//

// Font files supporting the specifications here
// are to be loaded by the frontend that is loading this module

export interface FusionFontStyle {
	fontFamily: Property.FontFamily
	fontWeight: Property.FontWeight
}

export interface FusionFontStyles {
	body: FusionFontStyle
	bodyBold: FusionFontStyle
	title: FusionFontStyle
	titleBold: FusionFontStyle
	mono: FusionFontStyle
	monoLight: FusionFontStyle
}

const fonts: FusionFontStyles = {
	body: {
		fontFamily: `'Source Sans Pro', sans-serif`,
		fontWeight: 400,
	},
	bodyBold: {
		fontFamily: `'Source Sans Pro', sans-serif`,
		fontWeight: 600,
	},
	title: {
		fontFamily: `'Ubuntu', sans-serif`,
		fontWeight: 400,
	},
	titleBold: {
		fontFamily: `'Ubuntu', sans-serif`,
		fontWeight: 700,
	},
	mono: {
		fontFamily: `'IBM Plex Mono', monospace`,
		fontWeight: 400,
	},
	monoLight: {
		fontFamily: `'IBM Plex Mono', monospace`,
		fontWeight: 200,
	}
}

//
//
// COLORS
//

export enum FusionColor {
	Inherit = 'inherit',
	White = 'white',
	Black = 'black',
	Transparent = 'transparent',
	Second = 'second',
	Third = 'third',
	Lines = 'lines',
	MiniHeader = 'miniHeader',
	InputLabel = 'inputLabel',
	TableLabel = 'tableLabel',
	Text = 'text',
	BlueLight2 = 'blueLight2',
	BlueLight1 = 'blueLight1',
	BlueMain = 'blueMain',
	BlueDark = 'blueDark',
	OrangeLight2 = 'orangeLight2',
	OrangeLight1 = 'orangeLight1',
	OrangeMain = 'orangeMain',
	Menu = 'menu',
	BloodOrange = 'bloodOrange',
	OrangeDark = 'orangeDark',
	ErrorRed = 'errorRed',
	StopRed = 'stopRed',
	GoGreen = 'goGreen',
	PdfGray = 'pdfGray',

	// these are in jon's component designs
	// TODO make these official in the palette...
	ThirdDisabled = 'thirdDisabled',
	TextDisabled = 'textDisabled',
	ShadowBg = 'shadowBg',

	// not official palette colors
	DxFuchsia = 'dxFuchsia', // in use for diagnoses
	ModalUnderlay = 'modalUnderlay',
	LogBg = 'logBg',
}

export type FusionColors = {
	[name in FusionColor]: string
}

const colors: FusionColors = {
	inherit: 'inherit',
	white: '#FFFFFF',
	black: '#000000',
	transparent: 'transparent',
	second: '#F0F3FA',
	third: '#E1E6F0',
	lines: '#C7CCD6',
	miniHeader: '#B5BDCC',
	inputLabel: '#8A92A1',
	tableLabel: '#4F5765',
	text: '#2E3A4E',
	blueLight2: '#D8E7F1',
	blueLight1: '#8CBAD8',
	blueMain: '#0F6DAA',
	blueDark: '#185780',
	orangeLight2: '#FDEDD9',
	orangeLight1: '#FACA93',
	orangeMain: '#F6911D',
	menu: '#DD7904',
	bloodOrange: '#F4680F',
	orangeDark: '#AF602F',
	errorRed: '#FFD8D8',
	stopRed: '#DD3E3E',
	goGreen: '#0FAA21',
	pdfGray: '#F2F2F2',
	thirdDisabled: 'rgba(225, 230, 240, 0.6)',
	textDisabled: 'rgba(46, 58, 78, 0.4)',
	shadowBg: 'rgba(15, 109, 170, 0.2)',
	dxFuchsia: '#ae00f0',
	modalUnderlay: 'rgba(0,0,0,.40)',
	logBg: 'rgba(0,0,0,.84)'
}

//
//
// BASE STYLES
//

export interface FusionBaseStyles {
	formRoot: CSSObject
	inputLabel: CSSObject
}

const baseStyles: FusionBaseStyles = {
	formRoot: {
		background: colors.white,
		...fonts.body,
		color: colors.text,
		'input': {
			...fonts.body,
			color: colors.text,
		},
		'hr': {
			border: 'none',
			height: '1px',
			backgroundColor: colors.lines,
		},
	},
	inputLabel: {
		fontSize: '14px',
		...fonts.bodyBold,
		color: colors.inputLabel,
	},
}

// for legible fonts when printing on headless Chrome in Linux container
const printBaseStyles: FusionBaseStyles = {
	formRoot: {
		background: colors.white,
		fontFamily: 'sans-serif',
		color: colors.black,
		'input': {
			fontFamily: 'sans-serif',
			color: colors.black,
		},
	},
	inputLabel: {
		fontSize: '14px',
		fontFamily: 'sans-serif',
		fontWeight: 'bold',
		color: colors.inputLabel,
	},
}

//
//
// LAYERS
//

// for interface elements in the root stacking context
// https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
export enum FusionInterfaceZIndex {
	Interface = 101,
	Header = 301,
	SideNav = 302,
	NavTrigger = 303,
	Modal = 401,
	Overlay = 501,
}

// for content elements,
// which can be within L2FixedInterfaceRoot's created stacking context,
// or within L2PrintInterfaceRoot, which does not create a stacking context
export enum FusionContentZIndex {
	Content = 201,
	Popup = 202,
}

// Quick replacement of mui's theme.spacing() function
// Specify CSS measurements in .5x or 1x multiples of 8px units
export type FusionSpacingFunc = (...n: number[]) => string
const spacingPx = 8
const spacing: FusionSpacingFunc = (...n: number[]): string => n.map(n => `${n * spacingPx}px`).join(' ')

export interface FusionThemeOverridable {
	spacingPx: number
	colors: FusionColors
	fonts: FusionFontStyles
	// TODO including this trips up the compiler in AppFormRelay,
	// something to do with CSSObject ?
	// baseStyles: FusionBaseStyles
	borderRadius: string
	borderRadius2: string
	boxShadow: string
	shadow2: string
	disabledOpacity: number
	isPrintMode: boolean
	statusBarHeightPx: number
}

export interface FusionTheme extends FusionThemeOverridable {
	spacing: FusionSpacingFunc
	baseStyles: FusionBaseStyles
}

export const fusionScreenTheme: FusionTheme = {
	spacing,
	spacingPx,
	colors,
	fonts,
	baseStyles,
	borderRadius: '10px',
	borderRadius2: '20px',
	// TODO standardize these with Jon...
	boxShadow: '0px 1px 7px rgba(6, 111, 170, 0.501961)',
	shadow2: `0px 4px 4px ${colors.shadowBg}`,
	disabledOpacity: 0.25,
	isPrintMode: false,
	statusBarHeightPx: 0,
}

export const fusionPrintThemeOverrides: Pick<FusionTheme, 'baseStyles'|'isPrintMode'> = {
	baseStyles: printBaseStyles,
	isPrintMode: true,
}
